<template>
  <div class="webinar-simulator">
    <b-card>
      <b-card-header>
        <h4 class="mb-0">
          <feather-icon icon="VideoIcon" class="mr-2" />
          Webinário
        </h4>
      </b-card-header>
      
      <b-card-body>
        <b-tabs content-class="mt-2" v-model="activeTab" @input="onTabChange">
          <!-- Aba de Autenticação -->
          <b-tab title="Autenticação">
            <template #title>
              <feather-icon icon="ShieldIcon" class="mr-1" />
              Autenticação YouTube
              <b-badge v-if="tabsCompleted.auth" variant="success" class="ml-1">✓</b-badge>
            </template>
            <YouTubeAuth
              v-if="isAuthenticated !== null"
              :is-authenticated="isAuthenticated"
              @auth-success="onAuthSuccess"
            />
          </b-tab>

          <!-- Aba de Configuração -->
          <b-tab title="Configuração" :disabled="!tabsCompleted.auth">
            <template #title>
              <feather-icon icon="SettingsIcon" class="mr-1" />
              Configuração
              <b-badge v-if="tabsCompleted.config" variant="success" class="ml-1">✓</b-badge>
            </template>
            <stream-config 
              :is-authenticated="isAuthenticated"
              :webinario-data="currentWebinario"
              @stream-ready="onStreamReady"
              @config-saved="onConfigSaved"
            />
          </b-tab>

          <!-- Aba de Chat -->
          <b-tab title="Chat Simulado" :disabled="!tabsCompleted.config">
            <template #title>
              <feather-icon icon="MessageSquareIcon" class="mr-1" />
              Chat Agendado
              <b-badge v-if="tabsCompleted.chat" variant="success" class="ml-1">✓</b-badge>
            </template>
            <chat-simulator 
              :is-streaming="isStreaming"
              :stream-key="streamConfig.streamKey"
              :video-file="streamConfig.videoFile"
              :video-url="streamConfig.videoUrl"
              :webinario-data="currentWebinario"
              @chat-saved="onChatSaved"
            />
          </b-tab>

          <!-- Aba de Triggers -->
          <b-tab title="Triggers do Chat" :disabled="!tabsCompleted.chat">
            <template #title>
              <feather-icon icon="HashIcon" class="mr-1" />
              Triggers do Chat
              <b-badge v-if="tabsCompleted.triggers" variant="success" class="ml-1">✓</b-badge>
            </template>
            <chat-triggers 
              :webinario-data="currentWebinario"
              @triggers-saved="onTriggersSaved"
            />
          </b-tab>

          <!-- Aba de Transmissão -->
          <b-tab title="Transmissão" :disabled="!tabsCompleted.triggers">
            <template #title>
              <feather-icon icon="PlayIcon" class="mr-1" />
              Transmissão
            </template>
            <stream-controls 
              :stream-config="streamConfig"
              :is-authenticated="isAuthenticated"
              @stream-status="onStreamStatus" 
            />
          </b-tab>
        </b-tabs>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BTabs, BTab, BBadge } from 'bootstrap-vue'
import { youtubeService } from '@/services/WebinarSimulator/youtubeService'
import YouTubeAuth from './components/YouTubeAuth.vue'
import StreamConfig from './components/StreamConfig.vue'
import StreamControls from './components/StreamControls.vue'
import ChatSimulator from './components/ChatSimulator.vue'
import ChatTriggers from './components/ChatTriggers.vue'

export default {
  name: 'WebinarSimulator',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTabs,
    BTab,
    BBadge,
    YouTubeAuth,
    StreamConfig,
    StreamControls,
    ChatSimulator,
    ChatTriggers,
  },
  data() {
    return {
      isAuthenticated: null,
      streamConfig: {
        streamKey: '',
        videoFile: null,
        videoUrl: '', // Adiciona videoUrl no streamConfig inicial
        videoKeyName: '',
        quality: '1080p',
        bitrate: 3000,
        fps: 30,
      },
      isStreaming: false,
      activeTab: 0,
      tabsCompleted: {
        auth: false,
        config: false,
        chat: false,
        triggers: false,
      },
      webinarios: [], // Lista de webinários do usuário
      currentWebinario: null, // Webinário atual selecionado
    }
  },
  watch: {
    isAuthenticated(newValue) {
      if (newValue) {
        this.tabsCompleted.auth = true
      }
    },
  },
  mounted() {
    this.checkYouTubeIntegration()
    this.loadStreamConfig()
    this.loadUserWebinarios()
  },
  methods: {
    async checkYouTubeIntegration() {
      try {
        const userData = localStorage.getItem('userData')
        if (!userData) return
        const userId = JSON.parse(userData).id
        if (!userId) return
        this.isAuthenticated = await youtubeService.checkIntegration(userId)
        console.log('Status de autenticação:', this.isAuthenticated)
      } catch (e) {
        console.error('Integração não encontrada')
      }
    },
    loadStreamConfig() {
      const saved = localStorage.getItem('stream_config')
      console.log('Tentando carregar configuração do localStorage:', saved)
      
      if (saved) {
        const savedConfig = JSON.parse(saved)
        console.log('Configuração parseada:', savedConfig)
        
        // Remove videoFile do saved (não pode ser serializado)
        const { videoFile, ...configWithoutFile } = savedConfig
        this.streamConfig = { ...this.streamConfig, ...configWithoutFile }
        
        console.log('StreamConfig atualizado:', this.streamConfig)
        
        // NÃO marca automaticamente como completa - usuário deve salvar
      } else {
        console.log('Nenhuma configuração salva encontrada')
      }
    },
    onAuthSuccess() {
      this.isAuthenticated = true
      this.tabsCompleted.auth = true
      this.$toast.success('Autenticação com YouTube realizada com sucesso!')
    },
    onStreamReady(config) {
      this.streamConfig = { ...this.streamConfig, ...config }
    },
    onConfigSaved() {
      this.tabsCompleted.config = true
      this.$toast.success('Configuração salva com sucesso!')
    },
    onChatSaved() {
      this.tabsCompleted.chat = true
      this.$toast.success('Chat agendado salvo com sucesso!')
    },
    onTriggersSaved() {
      this.tabsCompleted.triggers = true
      this.$toast.success('Triggers do chat salvos com sucesso!')
    },
    onStreamStatus(status) {
      this.isStreaming = status.isStreaming
      if (status.isStreaming) {
        this.$toast.success('Transmissão iniciada com sucesso!')
      } else {
        this.$toast.info('Transmissão parada.')
      }
    },
    onTabChange(tabIndex) {
      // Impede navegação se a aba anterior não foi completada
      if (tabIndex === 1 && !this.tabsCompleted.auth) {
        this.$toast.warning('Complete a autenticação primeiro!')
        this.activeTab = 0
        return
      }
      if (tabIndex === 2 && !this.tabsCompleted.config) {
        this.$toast.warning('Complete a configuração primeiro!')
        this.activeTab = 1
        return
      }
      if (tabIndex === 3 && !this.tabsCompleted.chat) {
        this.$toast.warning('Complete o agendamento do chat primeiro!')
        this.activeTab = 2
        return
      }
      if (tabIndex === 4 && !this.tabsCompleted.triggers) {
        this.$toast.warning('Configure os triggers do chat primeiro!')
        this.activeTab = 3
        return
      }
      this.activeTab = tabIndex
    },
    async loadUserWebinarios() {
      try {
        const userData = localStorage.getItem('userData')
        if (!userData) {
          console.log('Dados do usuário não encontrados')
          return
        }
        
        const userId = JSON.parse(userData).id
        if (!userId) {
          console.log('ID do usuário não encontrado')
          return
        }

        this.webinarios = await youtubeService.getWebinariosByUser(userId)
        console.log('Webinários carregados:', this.webinarios)
        
        // Se houver webinários, carrega o primeiro (ou o mais recente)
        if (this.webinarios && this.webinarios.data && this.webinarios.data.length > 0) {
          [this.currentWebinario] = this.webinarios.data
          this.loadWebinarioData(this.currentWebinario)
        }
      } catch (error) {
        console.error('Erro ao carregar webinários:', error)
        // Não exibe toast de erro para não poluir a interface
      }
    },
    loadWebinarioData(webinario) {
      console.log('Carregando dados do webinário:', webinario)
      
      // Atualiza streamConfig com os dados do webinário
      this.streamConfig = {
        ...this.streamConfig,
        streamKey: webinario.stream_key || '',
        videoUrl: webinario.video_url || '',
        videoKeyName: webinario.video_key_name || '',
        quality: webinario.qualidade || '1080p',
        bitrate: parseInt(webinario.bitrate, 10) || 3000,
        fps: parseInt(webinario.fps, 10) || 30,
      }
      
      // Salva a configuração carregada no localStorage
      const configToSave = {
        title: webinario.titulo || '',
        description: webinario.descricao || '',
        streamKey: webinario.stream_key || '',
        videoUrl: webinario.video_url || '',
        videoKeyName: webinario.video_key_name || '',
        quality: webinario.qualidade || '1080p',
        bitrate: parseInt(webinario.bitrate, 10) || 3000,
        fps: parseInt(webinario.fps, 10) || 30,
        aiChatEnabled: webinario.resposta_ia === 1,
        aiKeyword: webinario.palavra_chave || '',
      }
      
      localStorage.setItem('stream_config', JSON.stringify(configToSave))
      console.log('Configuração do webinário salva no localStorage:', configToSave)
      
      // Salva as mensagens do chat se existirem (mas não marca como completa)
      if (webinario.chat && Array.isArray(webinario.chat)) {
        localStorage.setItem('chat_scheduled_messages', JSON.stringify(webinario.chat))
        console.log('Mensagens do chat salvas:', webinario.chat)
        // NÃO marca automaticamente como completa - usuário deve salvar
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.webinar-simulator {
  .card {
    border-radius: 0.5rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  }
  
  .card-header {
    border-radius: 0.5rem 0.5rem 0 0;
    h4 {
      margin: 0;
      font-weight: 600;
    }
  }
}
</style>